/**
 * The writing pad: staff paper, an ink layer, and live recognition.
 *
 * Layers, bottom to top:
 *   staff (VexFlow SVG)  ->  ledger guides (canvas)  ->  ink (canvas)  ->  markers (DOM)
 *
 * Recognition runs a short moment after each stroke finishes, so the pitch name
 * appears next to what you wrote without you having to press anything. The raw ink
 * stays just long enough to see what was written, then yields to clean notation.
 */

import { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from 'react';
import { InkEngine, prefersPencilOnly } from '../ink/InkEngine';
import { reflowInkPage, sameStaffLayout, type InkSession } from '../ink/session';
import { beginWriting, endWriting } from '../ink/writingActivity';
import type { InkTool, Stroke } from '../ink/types';
import type { RecognitionResult } from '../ink/recognize';
import { readPage } from '../ink/readPage';
import {
  ACCIDENTAL_CHOICES,
  applyOverrides,
  noteKey,
  pruneOverrides,
  type AccidentalOverride,
  type OverrideMap,
} from '../ink/overrides';
import { pitchName } from '../theory/pitch';
import { snapToStaffOffset, stepsToDeltaY } from '../ink/selection';
import { committableStrokeIds } from '../ink/symbolState';
import { exportFixture, parseFixture, serializeFixture } from '../ink/strokeFixtures';
import type { Clef, StaffLayout } from '../theory/staff';
import type { Letter } from '../theory/pitch';
import { StaffPaper } from './StaffPaper';
import { ResolvedNotes } from './ResolvedNotes';
import { Button, IconButton } from '../design/Button';
import { SegmentedControl, ToolbarToggles } from '../design/Navigation';
import { Checkbox } from '../design/Field';
import type { IconName } from '../design/Icon';

export interface WritingPadHandle {
  clear: () => void;
  undo: () => void;
  recognizeNow: () => RecognitionResult | null;
  getStrokes: () => Stroke[];
  getSelection: () => string[];
  /** Staff geometry the ink was measured against — needed to persist it. */
  getLayout: () => StaffLayout | null;
  getOverrides: () => OverrideMap;
  /**
   * Replace the page wholesale. Used when reopening saved work: strokes and the
   * manual accidental corrections that belong to them are restored together, or
   * the corrections would be pruned as orphans on the next recognition pass.
   */
  restore: (strokes: Stroke[], overrides: OverrideMap) => void;
}

export interface WritingPadProps {
  clef: Clef;
  keySignature?: string;
  keySignatureMap?: Partial<Record<Letter, number>>;
  height?: number;
  /** Enlarges the engraved staff inside the dedicated full-screen workspace. */
  studio?: boolean;
  /** Recognise automatically after each stroke. */
  auto?: boolean;
  showMarkers?: boolean;
  onResult?: (result: RecognitionResult, layout: StaffLayout) => void;
  /** Extra controls rendered into the toolbar's right side. */
  toolbarExtra?: React.ReactNode;
  hint?: string;
  /**
   * The page this pad is a view of.
   *
   * Without one the pad owns its own writing, which is right for a single mount
   * point. With one, the page outlives teardown and document switching without
   * losing a stroke, an undo step or a corrected accidental.
   */
  session?: InkSession;
}

/**
 * One manuscript pen, deliberately not a preference.
 *
 * A learner should never have to wonder whether a misread came from a colour or
 * thickness setting, so there is exactly one pen and it is not a preference.
 */
const MANUSCRIPT_INK = '#12161d';
/**
 * 7.5px, and not adjustable.
 *
 * 4.6 was still too thin against a bright manuscript on a physical iPad — the
 * lightest normal Pencil pressure disappeared. 7.5 is clearly visible at the
 * lightest touch and still leaves the counter of a small notehead open, which is
 * the constraint pulling the other way: the recogniser has to be able to tell a
 * filled head from an open one, and a modifier line from a scribble.
 *
 * Exported so `InkEngine`'s default and the stored session preference can be
 * asserted against the same number rather than drifting to three values.
 */
export const MANUSCRIPT_WIDTH = 7.5;
/**
 * How long the learner's own ink stays before the engraved note takes over.
 *
 * Long enough to see what you wrote and compare it with what the app read —
 * which is the whole pedagogy — and short enough that the manuscript does not
 * fill up with two copies of every note.
 */
const INK_GRACE_MS = 55;
const INK_FADE_MS = 95;
/**
 * Ink the app could not read stays a little longer.
 *
 * Long enough to read the message and see which mark it is about, and still
 * short enough that the manuscript does not fill up with rejected strokes.
 */
const INK_UNREAD_GRACE_MS = 1200;
/** One completed notehead should feel immediate, not like a submitted form. */
export const NOTE_SNAP_MS = 45;

const TOOL_OPTIONS: { value: InkTool; label: string; icon: IconName }[] = [
  { value: 'pen', label: 'Pen', icon: 'pen' },
  { value: 'eraser', label: 'Eraser', icon: 'eraser' },
  { value: 'lasso', label: 'Select', icon: 'lasso' },
];

export const WritingPad = forwardRef<WritingPadHandle, WritingPadProps>(function WritingPad(
  {
    clef,
    keySignature = 'C',
    keySignatureMap,
    height = 340,
    studio = false,
    auto = true,
    showMarkers = true,
    onResult,
    toolbarExtra,
    hint,
    session,
  },
  ref,
) {
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const engineRef = useRef<InkEngine | null>(null);
  const layoutRef = useRef<StaffLayout | null>(null);
  const timerRef = useRef<number | null>(null);
  const fadeTimersRef = useRef<Set<number>>(new Set());
  /**
   * Layout work that arrived while the pen was down.
   *
   * Resizing the canvas rebuilds its backing store and repaints; reflowing the
   * staff re-hydrates every stroke from staff-relative units. Both are correct
   * between strokes and catastrophic during one — the live stroke is discarded
   * and the ink visibly jumps. Safari fires exactly these while you write, as
   * its toolbars collapse, so the work is queued and applied on the way out.
   */
  const pendingResizeRef = useRef<{ width: number; height: number } | null>(null);
  const pendingLayoutRef = useRef<StaffLayout | null>(null);
  /**
   * Safari's retracting chrome changes the visible height without rotating the
   * iPad. A notation canvas cannot follow that motion: changing its coordinate
   * system after every stroke is the "shaking staff" defect. Studio locks the
   * first rendered geometry and only accepts another one when orientation flips.
   */
  const studioGeometryRef = useRef<{
    width: number;
    height: number;
    orientation: 'portrait' | 'landscape';
  } | null>(null);
  const drawingRef = useRef(false);
  const onResultRef = useRef(onResult);
  onResultRef.current = onResult;
  // Read through a ref: the engine effect runs once, and must not be torn down
  // and rebuilt because a parent re-rendered with a new object identity.
  const sessionRef = useRef(session);
  sessionRef.current = session;

  const [size, setSize] = useState({ width: 0, height });
  // The active tool and accidental corrections are seeded from the session, so
  // a learner who picked the highlighter and corrected a note to a flat still
  // has both after expanding the staff. Pen colour and width are intentionally
  // fixed: they are part of MusicMentor's manuscript, not user preferences.
  const [tool, setTool] = useState<InkTool>(() => {
    const stored = session?.preferences.tool ?? 'pen';
    // Highlighter was a colour/thickness branch on a manuscript that now has
    // one deliberate black pen. Old sessions still open; they simply return to
    // the pen instead of exposing a retired tool.
    return stored === 'highlighter' ? 'pen' : stored;
  });
  // On an iPad the whole point is to rest your hand while writing, so Pencil-only
  // starts on. On a laptop that would leave you unable to draw at all.
  const [penOnly, setPenOnly] = useState(() => session?.preferences.penOnly ?? prefersPencilOnly());
  const [overrides, setOverrides] = useState<OverrideMap>(() => session?.overrides ?? {});
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const overridesRef = useRef<OverrideMap>({});
  overridesRef.current = overrides;
  const [result, setResult] = useState<RecognitionResult | null>(null);
  const resultRef = useRef<RecognitionResult | null>(null);
  const [layout, setLayout] = useState<StaffLayout | null>(null);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [selection, setSelection] = useState<string[]>([]);
  /** "I couldn't read that" — shown only while unreadable ink is still on screen. */
  const [unreadNotice, setUnreadNotice] = useState(false);

  /**
   * Hand a set of strokes over to the notation layer after a grace period.
   *
   * Every timer is tracked so unmounting cannot leave one running against a
   * canvas that no longer exists, and the engine is looked up *when the timer
   * fires* rather than captured — the page may have been reopened on another
   * canvas in the meantime, and the stroke ids are the same in both.
   */
  const scheduleFade = useCallback((ids: string[], delay: number) => {
    const timer = window.setTimeout(() => {
      fadeTimersRef.current.delete(timer);
      engineRef.current?.fadeOut(ids, INK_FADE_MS);
      setUnreadNotice(false);
    }, delay);
    fadeTimersRef.current.add(timer);
  }, []);

  const runRecognition = useCallback((): RecognitionResult | null => {
    const engine = engineRef.current;
    const layout = layoutRef.current;
    if (!engine || !layout) return null;
    const raw = readPage(engine.getStrokes(), {
      clef,
      layout,
      keySignature: keySignatureMap,
    });

    // Drop overrides whose ink has been erased, then apply what remains. Manual
    // corrections outrank both the drawn accidental and the key signature.
    const surviving = pruneOverrides(overridesRef.current, raw);
    if (Object.keys(surviving).length !== Object.keys(overridesRef.current).length) {
      overridesRef.current = surviving;
      setOverrides(surviving);
    }
    const next = applyOverrides(raw, surviving, clef);

    // Keep the accidental dock on the note that just arrived. A learner may
    // deliberately tap an older note afterward, so only a genuinely new note
    // moves the selection; ordinary re-recognition never steals it back.
    const previousKeys = new Set(resultRef.current?.notes.map(noteKey) ?? []);
    const newNotes = next.notes.filter((note) => !previousKeys.has(noteKey(note)));
    const liveKeys = new Set(next.notes.map(noteKey));
    if (newNotes.length) {
      const strokeOrder = new Map(engine.getStrokes().map((stroke, index) => [stroke.id, index]));
      const latest = [...newNotes].sort((a, b) => {
        const aOrder = Math.max(...a.strokeIds.map((id) => strokeOrder.get(id) ?? -1));
        const bOrder = Math.max(...b.strokeIds.map((id) => strokeOrder.get(id) ?? -1));
        return bOrder - aOrder;
      })[0];
      setSelectedKey(noteKey(latest));
    } else {
      setSelectedKey((current) =>
        current && liveKeys.has(current)
          ? current
          : next.notes.length
            ? noteKey(next.notes[next.notes.length - 1])
            : null,
      );
    }

    setResult(next);
    resultRef.current = next;
    setLayout(layout);

    /*
     * The ink hand-off, in one place, following one contract.
     *
     * `symbolState.ts` has described this lifecycle for a while and nothing was
     * using it: `runRecognition` collected *every* pen stroke and faded the lot.
     * So handwriting the app had failed to read disappeared exactly as though it
     * had been engraved — the learner saw their mark vanish with nothing to
     * replace it, which is the one failure the grace period exists to prevent.
     *
     * Two kinds of ink, two clear fates:
     */
    const strokes = engine.getStrokes().filter((stroke) => stroke.tool === 'pen');
    const committable = new Set(committableStrokeIds(next));

    // A confident reading gets a short grace period, then becomes the engraved
    //    note above it. The strokes stay in the document for undo, save and
    //    sync; only the painting stops.
    const confident = strokes
      .filter((stroke) => committable.has(stroke.id))
      .map((stroke) => stroke.id);
    if (confident.length) scheduleFade(confident, INK_GRACE_MS);

    /*
     * 3. Anything else is ink the app could not turn into notation. It still
     *    fades, because raw marks piling up across the manuscript is its own
     *    defect — but it is never silently replaced by a guess, and the learner
     *    is told rather than left wondering where their note went.
     */
    const unreadable = strokes
      .filter((stroke) => !committable.has(stroke.id))
      .map((stroke) => stroke.id);
    if (unreadable.length) {
      setUnreadNotice(true);
      scheduleFade(unreadable, INK_UNREAD_GRACE_MS);
    } else {
      setUnreadNotice(false);
    }

    onResultRef.current?.(next, layout);
    return next;
  }, [clef, keySignatureMap, scheduleFade]);

  const runRecognitionRef = useRef(runRecognition);
  runRecognitionRef.current = runRecognition;

  // -- size ------------------------------------------------------------
  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;
    studioGeometryRef.current = null;
    const measure = () => {
      const bounds = wrap.getBoundingClientRect();
      const width = Math.round(bounds.width || wrap.clientWidth);
      let measuredHeight = Math.round(bounds.height || wrap.clientHeight || height);
      const orientation = width >= measuredHeight ? 'landscape' : 'portrait';
      if (studio) {
        const locked = studioGeometryRef.current;
        if (locked && locked.orientation === orientation) {
          // Browser chrome changes height; Split View changes width. Ignore the
          // former so the staff never hops vertically, but accept the latter so
          // the studio still fits when the learner resizes the app beside a
          // second window.
          if (Math.abs(locked.width - width) <= 2) return;
          measuredHeight = locked.height;
        }
        studioGeometryRef.current = { width, height: measuredHeight, orientation };
      }
      // Safari can emit a storm of identical ResizeObserver entries while its
      // visual viewport is settling. Returning the same object prevents those
      // entries from rebuilding the canvas backing store and repainting the app.
      setSize((current) =>
        current.width === width && current.height === measuredHeight
          ? current
          : { width, height: measuredHeight },
      );
    };
    const observer = new ResizeObserver(measure);
    observer.observe(wrap);
    measure();
    return () => observer.disconnect();
  }, [height, studio]);

  // -- engine ----------------------------------------------------------
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const engine = new InkEngine(canvas, {
      onGestureStart: () => {
        drawingRef.current = true;
        beginWriting();
      },
      onGestureEnd: () => {
        drawingRef.current = false;
        endWriting();
        // Everything the world tried to do while the pen was down, now.
        const size = pendingResizeRef.current;
        if (size) {
          pendingResizeRef.current = null;
          engine.resize(size.width, size.height);
        }
        const layout = pendingLayoutRef.current;
        if (layout) {
          pendingLayoutRef.current = null;
          applyLayoutRef.current(layout);
        }
      },
      onStrokesChanged: () => {
        setCanUndo(engine.canUndo());
        setCanRedo(engine.canRedo());
      },
      onStrokeEnd: () => {
        if (timerRef.current) window.clearTimeout(timerRef.current);
        // The live pad recognises noteheads only. There is no multi-stroke
        // accidental to wait for, so a short fixed window feels immediate.
        timerRef.current = window.setTimeout(() => runRecognitionRef.current(), NOTE_SNAP_MS);
      },
      onSelectionChanged: (ids) => setSelection(ids),
      // A dragged selection has to be re-read immediately, not on a debounce:
      // until it is, the engraved note and the pitch label still describe where
      // the ink used to be, and "Snap to staff" would anchor on a stale y.
      onEdit: () => runRecognitionRef.current(),
    });
    engineRef.current = engine;

    // Take the page from the session, if there is one. The detach it hands back
    // reads the page out again, and must run *before* `destroy` — after it, the
    // engine has already let go of its canvas.
    const detach = sessionRef.current?.attach(engine);

    setCanUndo(engine.canUndo());
    setCanRedo(engine.canRedo());
    setSelection(engine.getSelection());

    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current);
      for (const fadeTimer of fadeTimersRef.current) window.clearTimeout(fadeTimer);
      fadeTimersRef.current.clear();
      detach?.();
      engine.destroy();
      if (drawingRef.current) {
        // `destroy` ends the gesture, but the flag is this component's and the
        // callback may not run after unmount. Balance the global count here or
        // the app stays "writing" forever and navigation stops responding.
        drawingRef.current = false;
        endWriting();
      }
      engineRef.current = null;
    };
  }, []);

  /**
   * Hand the learner's settings and corrections back to the session as they
   * change, rather than only on unmount. Unmount is not a reliable moment: a
   * page that is being torn down for any other reason still has to leave the
   * session in the state the learner last saw.
   */
  useEffect(() => {
    const held = sessionRef.current;
    if (!held) return;
    held.preferences = {
      tool,
      color: MANUSCRIPT_INK,
      width: MANUSCRIPT_WIDTH,
      penOnly,
    };
  }, [tool, penOnly]);

  useEffect(() => {
    const held = sessionRef.current;
    if (held) held.overrides = overrides;
  }, [overrides]);

  useEffect(() => {
    if (size.width <= 0) return;
    // Never between pointerdown and the end of that stroke.
    if (drawingRef.current) {
      pendingResizeRef.current = { width: size.width, height: size.height };
      return;
    }
    engineRef.current?.resize(size.width, size.height);
  }, [size]);

  useEffect(() => {
    const engine = engineRef.current;
    if (!engine) return;
    engine.tool = tool;
    engine.color = MANUSCRIPT_INK;
    engine.width = MANUSCRIPT_WIDTH;
    engine.penOnly = penOnly;

    if (tool !== 'lasso') {
      engine.clearSelection();
      runRecognitionRef.current();
    }
  }, [tool, penOnly]);

  /**
   * Sync the selection after a toolbar edit. Re-reading the page is not done
   * here — the engine's `onEdit` already fires for every edit, including the
   * ones made by dragging, which no button can hook.
   */
  const afterSelectionEdit = useCallback(() => {
    const engine = engineRef.current;
    if (!engine) return;
    setSelection(engine.getSelection());
  }, []);

  const nudgeSelection = useCallback(
    (dx: number, steps: number) => {
      const engine = engineRef.current;
      const layout = layoutRef.current;
      if (!engine) return;
      // Vertical nudges are in diatonic steps, not pixels: moving a chord "up
      // one" means up one line-or-space, which is the unit the staff is built
      // from. `stepsToDeltaY` owns the sign.
      const dy = layout ? stepsToDeltaY(steps, layout) : -steps * 8;
      engine.moveSelection(dx, dy);
      afterSelectionEdit();
    },
    [afterSelectionEdit],
  );

  /**
   * Drop the selection onto the nearest lines and spaces. Anchored on the
   * recognised notehead when there is one — that is the mark whose position
   * carries the pitch — and on the ink's centre otherwise.
   */
  const snapSelection = useCallback(() => {
    const engine = engineRef.current;
    const layout = layoutRef.current;
    if (!engine || !layout) return;
    const ids = new Set(engine.getSelection());
    if (!ids.size) return;

    const anchorNote = resultRef.current?.notes.find((n) =>
      n.strokeIds.some((id) => ids.has(id)),
    );
    let anchorY: number;
    if (anchorNote) {
      anchorY = anchorNote.y;
    } else {
      const bounds = engine.getSelectionBounds();
      if (!bounds) return;
      anchorY = (bounds.minY + bounds.maxY) / 2;
    }

    engine.moveSelection(0, snapToStaffOffset(anchorY, layout));
    afterSelectionEdit();
  }, [afterSelectionEdit]);

  // Re-read the ink when the clef changes: the same marks mean different pitches.
  useEffect(() => {
    if (!auto) return;
    runRecognitionRef.current();
  }, [clef, keySignature, auto]);

  useImperativeHandle(ref, () => ({
    clear: () => {
      engineRef.current?.clear();
      setResult(null);
      setUnreadNotice(false);
      const layout = layoutRef.current;
      if (layout) {
        const empty: RecognitionResult = {
          notes: [],
          groups: [],
          glyphs: [],
          unrecognizedStrokeIds: [],
          confidence: 0,
        };
        onResultRef.current?.(empty, layout);
      }
    },
    undo: () => {
      engineRef.current?.undo();
      runRecognitionRef.current();
    },
    recognizeNow: () => runRecognitionRef.current(),
    getStrokes: () => engineRef.current?.getStrokes() ?? [],
    getSelection: () => engineRef.current?.getSelection() ?? [],
    getLayout: () => layoutRef.current,
    getOverrides: () => overridesRef.current,
    restore: (strokes: Stroke[], restoredOverrides: OverrideMap) => {
      // Overrides first: `setStrokes` triggers a repaint and the recognition pass
      // that follows prunes any override whose ink it cannot find.
      overridesRef.current = restoredOverrides;
      setOverrides(restoredOverrides);
      setSelectedKey(null);
      engineRef.current?.setStrokes(strokes);
      runRecognitionRef.current();
    },
  }));

  const applyLayout = useCallback((layout: StaffLayout) => {
    const previous = layoutRef.current;
    layoutRef.current = layout;
    const engine = engineRef.current;
    if (!engine) return;
    // Compact and immersive staffs have different pixel grids. The session
    // reflows every stroke and every undo snapshot through staff units before
    // handing the page to this layout, so ink cannot drop below the staff.
    const held = sessionRef.current;
    if (held) held.reflow(engine, layout);
    else {
      if (previous && !sameStaffLayout(previous, layout)) {
        engine.hydrate(reflowInkPage(engine.serialize(), previous, layout));
      }
      engine.layout = layout;
    }
    if (auto) runRecognitionRef.current();
  }, [auto]);

  const applyLayoutRef = useRef(applyLayout);
  applyLayoutRef.current = applyLayout;

  /**
   * The staff re-measured. If the pen is down, remember it and do nothing:
   * re-hydrating the page mid-stroke throws away the stroke being drawn and
   * moves every mark on the manuscript under the learner's hand.
   */
  const handleLayout = useCallback((layout: StaffLayout) => {
    if (drawingRef.current) {
      pendingLayoutRef.current = layout;
      return;
    }
    applyLayoutRef.current(layout);
  }, []);

  /**
   * Development helpers. Recognition is deterministic geometry, so a misread on a
   * real iPad is fully reproducible from the strokes that caused it — provided
   * they can be got off the device. Export writes them out with the staff layout
   * (without which the coordinates mean nothing); import replays them.
   */
  const exportStrokes = useCallback(() => {
    const engine = engineRef.current;
    const layout = layoutRef.current;
    if (!engine || !layout) return;
    const fixture = exportFixture(engine.getStrokes(), layout, clef, {
      readAs: resultRef.current?.notes.map((n) => pitchName(n.pitch) + n.pitch.octave),
    });
    const blob = new Blob([serializeFixture(fixture)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `strokes-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }, [clef]);

  const importStrokes = useCallback(async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    try {
      const fixture = parseFixture(await file.text());
      engineRef.current?.setStrokes(fixture.strokes);
      runRecognitionRef.current();
    } catch (error) {
      // Developer-facing, and better than a silent no-op.
      window.alert(`Could not import: ${(error as Error).message}`);
    }
    event.target.value = '';
  }, []);

  /** Force an accidental on one note, then re-read so everything downstream updates. */
  const setOverride = useCallback(
    (key: string, value: AccidentalOverride) => {
      const next: OverrideMap = { ...overridesRef.current };
      if (value === null) delete next[key];
      else next[key] = value;
      overridesRef.current = next;
      setOverrides(next);
      setSelectedKey(key);
      runRecognitionRef.current();
    },
    [],
  );

  return (
    <div className="pad">
      <div className="pad-toolbar" role="toolbar" aria-label="Writing tools">
        {/* A pen is held down rather than chosen from a form, so these are
            pressed buttons in a toolbar. Arrow keys move between them. */}
        <ToolbarToggles
          label="Writing tool"
          value={tool}
          onChange={setTool}
          options={TOOL_OPTIONS}
        />

        <div className="mm-cluster pad-toolbar__actions">
          <IconButton
            icon="undo"
            label="Undo"
            disabled={!canUndo}
            onClick={() => {
              engineRef.current?.undo();
              runRecognition();
            }}
          />
          <IconButton
            icon="redo"
            label="Redo"
            disabled={!canRedo}
            onClick={() => {
              engineRef.current?.redo();
              runRecognition();
            }}
          />
          <Button
            variant="tertiary"
            onClick={() => {
              engineRef.current?.clear();
              runRecognition();
            }}
          >
            Clear
          </Button>
          <Checkbox
            label="Pencil only"
            checked={penOnly}
            onChange={(e) => setPenOnly(e.target.checked)}
          />

          {/* Development only: turn a real misread on an iPad into a fixture. */}
          {import.meta.env.DEV && !studio ? (
            <>
              <Button variant="tertiary" icon="download" title="Export strokes as JSON" onClick={exportStrokes}>
                Export
              </Button>
              <label className="mm-button mm-button--tertiary" title="Import a stroke fixture">
                Import
                <input
                  type="file"
                  accept="application/json"
                  style={{ display: 'none' }}
                  onChange={importStrokes}
                />
              </label>
            </>
          ) : null}
        </div>

        {toolbarExtra ? <div className="mm-cluster mm-cluster--end">{toolbarExtra}</div> : null}
      </div>

      <div ref={wrapRef} className="pad-surface" style={{ height }}>
        {size.width > 0 ? (
          <StaffPaper
            clef={clef}
            keySignature={keySignature}
            width={size.width}
            height={size.height}
            studio={studio}
            onLayout={handleLayout}
          />
        ) : null}
        <canvas
          ref={canvasRef}
          className="ink-canvas"
          role="application"
          aria-label={`Writing surface on a ${clef} staff`}
        />
        {showMarkers && result ? (
          <ResolvedNotes
            groups={result.groups}
            layout={layout}
            width={size.width}
            height={size.height}
          />
        ) : null}
        {unreadNotice ? (
          <p className="pad-notice" role="status">
            I couldn’t read that. Try the note again.
          </p>
        ) : null}
        {hint && !unreadNotice && (!result || result.notes.length === 0) ? (
          <p className="pad-hint">{hint}</p>
        ) : null}
      </div>

      {tool === 'lasso' ? (
        <div className="selection-bar" role="group" aria-label="Selected writing">
          {selection.length ? (
            <>
              <span className="inspector-label">
                {selection.length} mark{selection.length === 1 ? '' : 's'} selected
              </span>
              <div className="mm-cluster">
                <IconButton icon="arrowUp" label="Move up one line or space"
                  onClick={() => nudgeSelection(0, 1)} />
                <IconButton icon="arrowDown" label="Move down one line or space"
                  onClick={() => nudgeSelection(0, -1)} />
                <IconButton icon="arrowLeft" label="Move left"
                  onClick={() => nudgeSelection(-12, 0)} />
                <IconButton icon="arrowRight" label="Move right"
                  onClick={() => nudgeSelection(12, 0)} />
              </div>
              <div className="mm-cluster">
                <Button variant="tertiary" icon="snap"
                  title="Drop it onto the nearest line or space" onClick={snapSelection}>
                  Snap to staff
                </Button>
                <IconButton icon="plus" label="Make the selection bigger"
                  onClick={() => {
                    engineRef.current?.scaleSelection(1.15);
                    afterSelectionEdit();
                  }} />
                <IconButton icon="minus" label="Make the selection smaller"
                  onClick={() => {
                    engineRef.current?.scaleSelection(1 / 1.15);
                    afterSelectionEdit();
                  }} />
                <IconButton icon="duplicate" label="Duplicate the selection"
                  onClick={() => {
                    engineRef.current?.duplicateSelection();
                    afterSelectionEdit();
                  }} />
                <IconButton icon="trash" label="Delete the selected writing"
                  onClick={() => {
                    engineRef.current?.deleteSelection();
                    afterSelectionEdit();
                  }} />
                <Button variant="tertiary" onClick={() => {
                  engineRef.current?.clearSelection();
                  setSelection([]);
                }}>
                  Deselect
                </Button>
              </div>
            </>
          ) : (
            <span className="inspector-label">
              Circle some writing to select it, then drag it — it steps from line to space.
            </span>
          )}
        </div>
      ) : null}

      {studio || (result && result.notes.length) ? (
        <div className={`note-inspector ${studio ? 'is-studio-dock' : ''}`}>
          {result?.notes.length ? (
            <>
          <div className="inspector-notes" role="group" aria-label="Recognised notes">
            {result.notes.map((note) => {
              const key = noteKey(note);
              const isSelected = key === (selectedKey ?? noteKey(result.notes[result.notes.length - 1]));
              return (
                <button
                  key={key}
                  type="button"
                  className={`mm-note-chip ${
                    overrides[key] !== undefined && overrides[key] !== null ? 'is-edited' : ''
                  }`}
                  aria-pressed={isSelected}
                  onClick={() => setSelectedKey(key)}
                >
                  {pitchName(note.pitch)}
                  {note.pitch.octave}
                </button>
              );
            })}
          </div>

          <div className="inspector-accidental">
            <span className="inspector-label">Accidental</span>
            {(() => {
              const key = selectedKey ?? noteKey(result.notes[result.notes.length - 1]);
              const current = overrides[key] ?? null;
              return (
                <SegmentedControl
                  label="Accidental for the selected note"
                  value={String(current)}
                  onChange={(next) =>
                    setOverride(key, next === 'null' ? null : (Number(next) as AccidentalOverride))
                  }
                  options={ACCIDENTAL_CHOICES.map((choice) => ({
                    value: String(choice.value),
                    label: choice.label,
                  }))}
                />
              );
            })()}
          </div>
            </>
          ) : (
            <p className="studio-note-empty" role="status">
              <span className="manuscript-workspace__ready" aria-hidden="true" />
              Draw a note. Its pitch and ♮ ♯ ♭ controls will stay here.
            </p>
          )}
        </div>
      ) : null}
    </div>
  );
});
